import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { DotsThree } from "@phosphor-icons/react";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@libs/zapi-ui/shadcn";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useEditorStore } from "@/zapi-frontend/stores/editor";
import { parseSize } from "@/zapi-frontend/shared/lib/parseSize";
import { exportProject, type TExportProjectOptions } from "@/zapi-frontend/widgets/Header/lib/pdf-export";
import { ExportOptionsModal } from "@/zapi-frontend/widgets/Header/ui/_ExportOptionsModal";
import {
  useExportHistory,
  type TExportHistoryItem
} from "../ProjectOverviewIsland/hooks/useExportHistory";
import { SidebarExportIcon } from "@libs/zapi-ui/assets";
import { ProjectLoadingScreen } from "../ProjectLoadingScreen";

const FILE_TYPE_LABEL: Record<TExportHistoryItem["fileType"], string> = {
  pdf: "PDF",
  docx: "DOCX",
  md: "MD",
  pptx: "PPTX",
  presentation_pdf: "PDF"
};

function formatExportDate(value: string, lng: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(lng === "ja" ? "ja-JP" : "en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function ExportRow({ item }: { item: TExportHistoryItem }) {
  const { t, i18n } = useTranslation(["projectHome", "common"]);
  const isDeck = ["pptx", "presentation_pdf"].includes(item.fileType);
  const size = item.fileSize ? parseSize(item.fileSize) : null;
  const meta = [size ? `${size.value}${size.unit}` : null, `${isDeck ? t("exports.deck") : t("exports.memo")} · ${formatExportDate(item.createdAt, i18n.language)}`]
    .filter(Boolean)
    .join(" - ");

  const handleDownload = async () => {
    try {
      const response = await fetch(item.fileUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = item.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      toast.error(t("exports.downloadFailed"));
    }
  };

  return (
    <div className="flex items-center justify-between w-full py-3 pr-2 border-b border-innoveraZinc-150 bg-white">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <div className="relative h-9 w-7 shrink-0 rounded-[5px] border border-innoveraZinc-150 overflow-hidden">
          <span className="absolute inset-x-[-1px] bottom-[3px] text-center text-[8px] leading-[10px] font-medium tracking-[-0.16px] text-innoveraZinc-500">
            {FILE_TYPE_LABEL[item.fileType]}
          </span>
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-[14px] leading-5 font-medium text-innoveraZinc-900 truncate">{item.fileName}</span>
          <span className="text-[12px] leading-[18px] text-innoveraZinc-500 truncate">{meta}</span>
        </div>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="size-6 shrink-0 flex items-center justify-center rounded-md text-innoveraZinc-600 hover:bg-innoveraZinc-100 outline-none"
          >
            <DotsThree className="size-4" weight="bold" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem data-testid={`export-row-${item.id}-open-item`} onClick={() => window.open(item.fileUrl, "_blank")}>
            {t("exports.open")}
          </DropdownMenuItem>
          <DropdownMenuItem data-testid={`export-row-${item.id}-download-item`} onClick={handleDownload}>
            {t("exports.download")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function ExportsIsland() {
  const { t } = useTranslation(["projectHome", "common"]);
  const { id } = useParams();
  const projectId = typeof id === "string" ? id : "";
  const queryClient = useQueryClient();
  const { selectedProject } = useProjectStore();
  const { flushSave } = useEditorStore();

  const { data, isLoading } = useExportHistory(projectId, 100);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (options: TExportProjectOptions) => {
    setIsModalOpen(false);
    setIsExporting(true);
    const toastId = toast.loading(t("common:exporting"));
    try {
      await flushSave?.();
      await exportProject(selectedProject?.id as unknown as number, options);
      await queryClient.invalidateQueries({ queryKey: ["exportHistory", projectId] });
      toast.success(t("header.exportDocSuccess"), { id: toastId });
    } catch (error) {
      console.error("Export failed:", error);
      toast.error(t("header.exportDocFailed"), { id: toastId });
    } finally {
      setIsExporting(false);
    }
  };

  const exports = data?.exports ?? [];

  return (
    <div className="relative w-full min-w-0 h-full bg-white flex flex-col shadow-[0px_51px_14px_0px_rgba(24,24,27,0),0px_33px_13px_0px_rgba(24,24,27,0),0px_18px_11px_0px_rgba(24,24,27,0.01),0px_8px_8px_0px_rgba(24,24,27,0.01),0px_2px_5px_0px_rgba(24,24,27,0.02)]">
      <div className="h-14 shrink-0 border-b border-innoveraZinc-100 flex items-center px-4">
        <div className="flex-1 min-w-0">
          <span className="text-[16px] leading-[15px] font-medium text-innoveraZinc-900">{t("sidebar.exports")}</span>
        </div>
        <AppButton
          onClick={() => setIsModalOpen(true)}
          disabled={isExporting}
          isLoading={isExporting}
          loadingText={t("common:exporting")}
          startAdornment={<SidebarExportIcon className="text-white" />}
          data-testid="exports-page-export"
        >
          {t("common:export")}
        </AppButton>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4">
        {isLoading ? (
          <div className="h-full flex items-center justify-center">
            <ProjectLoadingScreen />
          </div>
        ) : exports.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <FigmaTypography variant="body-3" color="zinc500">
              {t("exports.empty")}
            </FigmaTypography>
          </div>
        ) : (
          <div className="flex flex-col w-full">
            {exports.map((item) => (
              <ExportRow key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>

      <ExportOptionsModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onExport={handleExport}
        isExporting={isExporting}
        projectName={selectedProject?.name || ""}
      />
    </div>
  );
}
